import { FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { getSchedulesByStation } from '../../actions/contentActions'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import tts from '../../lib/TTS.js'
import { store } from '../../store'
import { getSupportCode } from './utils.js'

import type { RootState } from '../../reducers/types/reducerTypes.js'

const COMPONENT_FOCUS_KEY = 'LIVE_TV_WITH_STATIONS'
const STATION_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_STATION_BUTTON`
const TITLE = 'Live TV Not Available'
const URL = 'www.pbs.org/livestreamhelp'

import type { Station } from './types'

type LiveTVWithStationsProps = {
  callSign: string
  stations: Array<Station>
  handleUserLocalized?: () => void
}

const LiveTVWithStations = ({
  callSign,
  stations,
}: LiveTVWithStationsProps) => {
  const [supportCode, setSupportCode] = useState<string>('')
  const [readWholeScreen, setReadWholeScreen] = useState<boolean>(false)
  const { ref, focusKey, hasFocusedChild } = useHybridFocusable({
    focusKey: COMPONENT_FOCUS_KEY,
    trackChildren: true,
  })
  const SUPPORT_CODE_TEXT = `Support Code: ${supportCode}`
  const stationCount = stations.length
  const MORE_MESSAGE_TEXT = `We found ${stationCount} stations that are in your broadcast area. Select one to start watching live:`

  useEffect(() => {
    getSupportCode().then((supportCode) => {
      setSupportCode(supportCode)
    })
    store.dispatch({
      type: 'SET_SCHEDULES',
      payload: {
        channels: [],
      },
    })
  }, [])

  useEffect(() => {
    if (!hasFocusedChild) {
      setReadWholeScreen(true)
    }
  }, [hasFocusedChild])

  const handleButtonFocus = (index: number, title: string) => {
    tts.stopRead()
    let buttonTts = `Button ${index + 1} of ${stationCount} .... ${title}.`
    if (readWholeScreen) {
      buttonTts = `${TITLE} .... The ${callSign} Livestream is not available because we detect that you may not be in the station broadcast coverage area. For more information, visit: ${URL} ${SUPPORT_CODE_TEXT} .... ${MORE_MESSAGE_TEXT} .... ${buttonTts}`
    }
    tts.addTextToRead(buttonTts)
    setReadWholeScreen(false)
  }

  const handleEnterPress = (station: Station) => () => {
    tts.stopRead()
    store.dispatch({
      type: 'UPDATE_FALLBACK_STATION',
      payload: {
        ...station,
      },
    })
    tts.pushTextToRead(`Selected ${station.shortName} station.`)
    getSchedulesByStation(
      station.cid,
      new Date().toISOString().split('T')[0],
      true
    )
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="livestream-screen__with-stations">
        <div className="livestream-screen__with-stations__wrapper">
          <h1 className="livestream-screen__with-stations__title">{TITLE}</h1>
          <div className="livestream-screen__with-stations__container">
            <p className="livestream-screen__with-stations__message">
              {`The ${callSign} Livestream is not available because we detect that you may not be in the station broadcast coverage area. For more information, visit: ${URL}`}
            </p>
            {supportCode && (
              <p className="livestream-screen__with-stations__message">
                {SUPPORT_CODE_TEXT}
              </p>
            )}
            <p className="more-message">{MORE_MESSAGE_TEXT}</p>
          </div>
          <div className="livestream-screen__with-stations__station-buttons">
            {stationCount > 0 &&
              stations.slice(0, 3).map((station, index) => (
                <FocusableButtonTS
                  className="livestream-screen__with-stations__button"
                  focusKey={index === 0 ? STATION_BUTTON_FOCUS_KEY : ''}
                  key={index}
                  onEnterPress={handleEnterPress(station)}
                  onFocus={() => handleButtonFocus(index, station.shortName)}
                >
                  {station.shortName}
                </FocusableButtonTS>
              ))}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    callSign: state.station.call_sign,
  }
}

export default connect(mapStateToProps, null)(LiveTVWithStations)
